// JSON Web Token Manager
import isObject from 'lodash.isobject'

import { State } from './state'
import { isTokenExpired, parseJWT } from './utils'
import { HTTP_STATUS_CODES, HTTP_METHOD, TokenData } from './constants/types'

import {
  AccessTokenRequestError,
  BrokenTokenError,
  RefreshTokenRequestError,
  OidcSecurityValidationError
} from '../../errors'


import type { IdToken } from './state'

export type TokenManagerConfig = {
  clientId: string,
  callbackUrl: string,
  endpoint: string
}

export interface Token {
  refreshToken: string
  accessToken: string
  idToken: string
  expiresIn: string | number
}

const enum GRANT_TYPE {
  AUTH_CODE = 'authorization_code',
  REFRESH_TOKEN = 'refresh_token'
}

const headers = {
  'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
}

export class TokenManager {
  public state: State
  private readonly config: TokenManagerConfig

  constructor(config: TokenManagerConfig, state: State) {
    this.config = config
    this.state = state
  }

  public async requestNewToken(codeVerifier: string, secret: string) {
    const form = new URLSearchParams

    if (!codeVerifier || !secret) {
      throw new AccessTokenRequestError('Missing code verifier or secret')
    }

    form.append('client_id', this.config.clientId)
    form.append('redirect_uri', this.config.callbackUrl)
    form.append('code', secret)
    form.append('code_verifier', codeVerifier)
    form.append('grant_type', GRANT_TYPE.AUTH_CODE)

    const response = await fetch(this.config.endpoint, {
      method: HTTP_METHOD.POST,
      headers,
      body: form.toString()
    })

    if (response.ok) {
      const token = await response.json() as TokenData
      const idToken = token.id_token ? parseJWT<IdToken>(token.id_token) : null

      if (idToken && idToken.nonce && this.state.data.nonce && idToken.nonce !== this.state.data.nonce) {
        throw new OidcSecurityValidationError('Nonce does not match')
      }

      return token
    } else if (response.status === 400) {
      throw new AccessTokenRequestError('Bad Request')
    }

    throw new AccessTokenRequestError(`Server status is "${response.status}".`)
  }

  private async refresh() {
    const form = new URLSearchParams

    if (!this.state.token?.refresh_token) {
      throw new RefreshTokenRequestError('Refresh token is missing', 0)
    }

    form.append('grant_type', GRANT_TYPE.REFRESH_TOKEN)
    form.append('refresh_token', this.state.token.refresh_token)
    form.append('client_id', this.config.clientId)

    const response = await fetch(this.config.endpoint, {
      method: HTTP_METHOD.POST,
      headers,
      body: form.toString()
    })

    if (response.ok) {
      return response.json()
    } else if (response.status === HTTP_STATUS_CODES.BAD_REQUEST) {
      throw new RefreshTokenRequestError('Bad Request', response.status)
    } else if (response.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
      throw new RefreshTokenRequestError('Unauthorized', response.status)
    }

    throw new RefreshTokenRequestError('Failed', response.status)
  }

  public async fetchToken() {
    if (this.state.token && isObject(this.state.token)) {
      const expiredAt = this.state.tokenDate('expiredAt')

      if (expiredAt && isTokenExpired(new Date(expiredAt))) {
        try {
          const response: TokenData = await this.refresh()
          this.state.token = response

          return this.state.token
        } catch (error) {
          if (error instanceof RefreshTokenRequestError) {
            switch (true) {
              case error.code >= HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR:
                throw error
              case [HTTP_STATUS_CODES.BAD_REQUEST, HTTP_STATUS_CODES.UNAUTHORIZED].includes(error.code):
                throw new BrokenTokenError
            }
          }
        }
      } else {
        return this.state.token
      }
    }

    const codeVerifier = this.state.data.code_verifier as string
    const secret = this.state.data.code as string

    return await this.requestNewToken(codeVerifier, secret)
  }
}
